import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import { ListItemIcon } from '@mui/material'
import Collapse from '@mui/material/Collapse'
import List from '@mui/material/List'
import MenuItem from '@mui/material/MenuItem'
import { styled } from '@mui/material/styles'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { ReactElement } from 'react'
import { MenuItemLink, MenuItemLinkProps, useTranslate, useSidebarState } from 'react-admin'
import { border, themeColor } from './themes'

const SubMenu = (props: Props): JSX.Element | null => {
  const translate = useTranslate()
  const [, setOpen] = useSidebarState()
  const {
    active = false,
    handleToggle,
    sidebarIsOpen,
    isOpen,
    icon,
    name,
    children,
    dense
  } = props

  // `sidebarIsOpen` is passed as `!open` by Menu, so true means the sidebar is collapsed.
  const collapsed = sidebarIsOpen
  // Mark the header as the current location when the active child itself isn't visible.
  const headerActive = active && (collapsed || !isOpen)

  const handleClick = () => {
    if (collapsed) {
      setOpen(true)
      if (!isOpen) handleToggle()
    } else {
      handleToggle()
    }
  }

  const header = (
    <MenuItem
      className={`${classes.header}${headerActive ? ` ${classes.headerActive}` : ''}`}
      dense={dense}
      onClick={handleClick}
      aria-expanded={collapsed ? undefined : isOpen}
    >
      {icon && (
        <ListItemIcon className={classes.icon}>
          {icon}
        </ListItemIcon>
      )}
      {!collapsed && (
        <Typography variant="inherit" className={classes.title}>
          {translate(name)}
        </Typography>
      )}
      {!collapsed && (
        <div className={classes.dropdown}>
          {isOpen ? <ExpandLess /> : <ExpandMore />}
        </div>
      )}
    </MenuItem>
  )

  return children ? (
    <Root className={classes.root}>
      {collapsed ? (
        <Tooltip title={translate(name)} placement="right">
          {header}
        </Tooltip>
      ) : (
        header
      )}
      <Collapse in={isOpen && !collapsed} timeout="auto" unmountOnExit>
        <List dense={dense} component="div" disablePadding className={classes.list}>
          {children}
        </List>
      </Collapse>
    </Root>
  ) : null
}

interface Props {
  /** True when the current route belongs to one of the children. */
  active?: boolean;
  dense: boolean;
  handleToggle: () => void;
  icon?: ReactElement;
  isOpen: boolean;
  name: string;
  sidebarIsOpen: boolean;
  children?: React.ReactNode;
}

const PREFIX = 'xxllncSubMenu'

const classes = {
  root: `${PREFIX}-root`,
  header: `${PREFIX}-header`,
  headerActive: `${PREFIX}-headerActive`,
  icon: `${PREFIX}-icon`,
  title: `${PREFIX}-title`,
  dropdown: `${PREFIX}-dropdown`,
  list: `${PREFIX}-list`,
  item: `${PREFIX}-item`,
}

// Geometry shared by header and children: the header icon (20px) sits at 12px
// padding, so its centre (and the guide line) is 22px from the item's left edge;
// child labels start at 12px padding + 32px icon column = 44px, the same column
// as the labels of top-level items.
const GUIDE_X = 22
const CHILD_INSET = 32

const focusRing = {
  outline: `2px solid ${themeColor.sidebarActiveText}`,
  outlineOffset: -2,
}

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.root}`]: {
    overflow: 'hidden',
  },
  [`& .${classes.header}`]: {
    borderRadius: border.radius.sm,
    display: 'flex',
    color: themeColor.sidebarText,
    padding: theme.spacing(1.25, 1.5),
    marginTop: 0,
    transition: 'background-color 0.15s ease, color 0.15s ease',
    [theme.breakpoints.up('sm')]: {
      height: 40,
      minHeight: 40,
    },
    // No background while open: only hover (and keyboard focus) get one.
    ['&:hover']: {
      [theme.breakpoints.up('sm')]: {
        backgroundColor: themeColor.sidebarHover,
        color: themeColor.sidebarTextHover,
      },
    },
    ['&.Mui-focusVisible']: {
      ...focusRing,
      backgroundColor: themeColor.sidebarHover,
    },
  },
  // Header selector included to beat MUI MenuItem's own `.MuiListItemIcon-root { min-width: 36px }`.
  [`& .${classes.header} .${classes.icon}`]: {
    minWidth: 32,
    color: 'inherit',
    ['& .MuiSvgIcon-root']: {
      fill: 'currentColor',
    },
  },
  [`& .${classes.headerActive}`]: {
    backgroundColor: themeColor.sidebarActive,
    color: themeColor.sidebarActiveText,
    ['&:hover']: {
      backgroundColor: themeColor.sidebarActive,
    },
  },
  [`& .${classes.title}`]: {
    fontSize: 14,
    fontWeight: 500,
    color: 'inherit',
  },
  [`& .${classes.dropdown}`]: {
    display: 'flex',
    marginLeft: 'auto',
    ['& .MuiSvgIcon-root']: {
      fill: 'currentColor',
      fontSize: 18,
    },
  },
  [`& .${classes.list}`]: {
    position: 'relative',
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(1),
    // Thin guide line under the header icon: the children read as parts of the header.
    ['&::before']: {
      content: '""',
      position: 'absolute',
      left: GUIDE_X,
      top: 0,
      bottom: theme.spacing(1),
      width: 1,
      backgroundColor: themeColor.sidebarBorder,
    },
  },
}))

// Styles for SubMenuItemLink, scoped to classes.item like the rest of this file.
const StyledSubMenuItemLink = styled(MenuItemLink)(({ theme }) => ({
  [`&.${classes.item}`]: {
    'position': 'relative',
    'marginLeft': CHILD_INSET,
    'padding': theme.spacing(1, 1.5),
    'minHeight': 40,
    'borderRadius': border.radius.sm,
    'color': themeColor.sidebarText,
    'fontSize': 14,
    'fontWeight': 400,
    'transition': 'background-color 0.15s ease, color 0.15s ease',
    '&:hover': {
      [theme.breakpoints.up('sm')]: {
        backgroundColor: themeColor.sidebarHover,
        color: themeColor.sidebarTextHover,
      },
    },
    '&.Mui-focusVisible': {
      ...focusRing,
      backgroundColor: themeColor.sidebarHover,
    },
    '&.RaMenuItemLink-active': {
      backgroundColor: 'transparent',
      color: themeColor.sidebarActiveText,
      fontWeight: 600,
    },
    '&.RaMenuItemLink-active:hover': {
      backgroundColor: themeColor.sidebarHover,
    },
    // Accent segment on the guide line, next to the active child.
    '&.RaMenuItemLink-active::before': {
      content: '""',
      position: 'absolute',
      left: GUIDE_X - CHILD_INSET - 1,
      top: theme.spacing(1),
      bottom: theme.spacing(1),
      width: 3,
      borderRadius: border.radius.sm,
      backgroundColor: themeColor.sidebarActiveText,
    },
  },
}))

/** Child link of a SubMenu: label on the label column, no icon, active = accent on the guide line. */
export const SubMenuItemLink = ({ className, ...props }: MenuItemLinkProps): JSX.Element => (
  <StyledSubMenuItemLink {...props} className={className ? `${classes.item} ${className}` : classes.item} />
)

export default SubMenu
